import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AboutThisData, { deliveredDays } from '../../components/data/AboutThisData';
import AvailableDatePicker from '../../components/data/AvailableDatePicker';
import FlowResultPanel from '../../components/data/FlowResultPanel';
import SourceBadge from '../../components/data/SourceBadge';
import LocationSearchBar from '../../components/common/LocationSearchBar';
import Map from '../../components/common/Map';
import NavBar from '../../components/common/NavBar';
import ChosenCountry from '../../components/data/ChosenCountry';
import CountryChooser from '../../components/data/CountryChooser';
import { COUNTRIES } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { capability } from '../../config/products';
import { useDataWindow } from '../../hooks/useDataWindow';
import { useChosenCountry } from '../../hooks/useChosenCountry';
import { useFlowLookup } from '../../hooks/useFlowLookup';
import { roundCoord } from '../../services/flowApi';
import { formatDayLabel } from '../../utils/dateWindow';
import { buildFlowSeries } from '../../utils/flow';
import { startPath } from '../../utils/productPaths';

interface Point {
  lat: number;
  lng: number;
}

const parseCoord = (raw: string | null, limit: number): number | null => {
  if (raw === null || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

const startView = (c: CountryCode) => ({ ...COUNTRIES[c].defaultView, pitch: 0, bearing: 0 });

interface FlowWorkspaceProps {
  country: CountryCode;
  onChangeCountry(): void;
}

const FlowWorkspace: React.FC<FlowWorkspaceProps> = ({ country, onChangeCountry }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is read once; after that the page state is the source and is written back.
  const [initial] = useState(() => {
    const lat = parseCoord(searchParams.get('lat'), 90);
    const lon = parseCoord(searchParams.get('lon'), 180);
    const point: Point | null = lat !== null && lon !== null ? { lat, lng: lon } : null;
    return { day: searchParams.get('day'), point };
  });

  const [point, setPoint] = useState<Point | null>(initial.point);
  const [view, setView] = useState<any>(() =>
    initial.point
      ? { ...startView(country), latitude: initial.point.lat, longitude: initial.point.lng, zoom: 15 }
      : startView(country),
  );

  const dw = useDataWindow({
    country,
    product: 'flow',
    geoType: null,
    initial: { day: initial.day },
  });
  const { state, run, retry, cancel } = useFlowLookup();

  // Keep the URL in step so a lookup can be shared or reloaded.
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  const day = dw.day;
  const pointLat = point?.lat;
  const pointLng = point?.lng;
  useEffect(() => {
    setParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('country', country);
        if (day) next.set('day', day);
        if (pointLat !== undefined && pointLng !== undefined) {
          next.set('lat', pointLat.toFixed(6));
          next.set('lon', pointLng.toFixed(6));
        } else {
          next.delete('lat');
          next.delete('lon');
        }
        return next;
      },
      { replace: true },
    );
  }, [country, day, pointLat, pointLng]);

  const windowStart = dw.window?.start_time;
  const windowEnd = dw.window?.end_time;
  const ready = dw.status === 'ready';
  useEffect(() => {
    if (pointLat === undefined || pointLng === undefined) {
      cancel();
      return;
    }
    if (ready && windowStart && windowEnd) {
      run({
        country,
        lat: pointLat,
        lon: pointLng,
        window: { start_time: windowStart, end_time: windowEnd },
      });
    }
  }, [country, pointLat, pointLng, ready, windowStart, windowEnd, run, cancel]);

  const dedupe = capability(country, 'flow')?.dedupeOverlappingBatches ?? false;
  const outcome = useMemo(
    () => (state.status === 'done' ? buildFlowSeries(state.rows, state.request.window, { dedupe }) : null),
    [state, dedupe],
  );

  const choosePoint = useCallback((lat: number, lng: number) => setPoint({ lat: roundCoord(lat), lng: roundCoord(lng) }), []);

  const label = COUNTRIES[country].label;
  const markers = useMemo(
    () => (point ? [{ id: 'point', position: [point.lng, point.lat] as [number, number] }] : []),
    [point],
  );

  return (
    <div className="min-h-screen bg-paper-100">
      <NavBar title="Flow" showBackButton backTo={startPath('flow')} />
      <main className="mx-auto max-w-7xl space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-ink">Traffic past a point</h1>
            <p className="text-sm text-ink-muted">
              People in cars passing a point on the road network, by hour and direction.
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <ChosenCountry country={country} onChange={onChangeCountry} className="py-1" />
            <div className="w-full sm:w-80">
              <AvailableDatePicker dw={dw} />
            </div>
            <SourceBadge country={country} product="flow" />
          </div>
        </div>

        <AboutThisData country={country} product="flow" dates={deliveredDays(dw)} />

        <div className="grid gap-4 md:grid-cols-[1fr_24rem]">
          <div className="relative h-[55vh] overflow-hidden rounded-2xl border border-paper-300 md:h-[calc(100vh-14rem)]">
            <Map
              viewState={view}
              onViewStateChange={setView}
              onMapClick={(p) => choosePoint(p.lat, p.lng)}
              markers={markers}
              initialView={COUNTRIES[country].defaultView}
            />
            <LocationSearchBar
              country={country}
              placeholder={`Search an address in ${label}`}
              onSearch={(lat, lng) => {
                choosePoint(lat, lng);
                setView((v: any) => ({ ...v, latitude: lat, longitude: lng, zoom: 16 }));
              }}
            />
          </div>
          <aside className="rounded-2xl border border-paper-300 bg-surface p-4">
            <FlowResultPanel
              lookup={state}
              outcome={outcome}
              country={country}
              dayLabel={day ? formatDayLabel(day) : ''}
              onRetry={retry}
            />
          </aside>
        </div>
      </main>
    </div>
  );
};

const FlowPage: React.FC = () => {
  const { country, choose, change } = useChosenCountry('flow');
  if (!country) return <CountryChooser product="flow" title="Flow" onChoose={choose} />;
  return <FlowWorkspace key={country} country={country} onChangeCountry={change} />;
};

export default FlowPage;
